<script setup lang="ts">
import useLocale from '../../composables/useLocale';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faBookOpen, faHeadphones } from '@fortawesome/pro-regular-svg-icons';
import { storeToRefs } from 'pinia';
import useReaderStore from '../../stores/reader';
import { FontAwesomeIconSizes, SwitchBookPlayerModeEvent } from '../../types';
import { useDisplay } from 'vuetify';
import { computed } from 'vue';
import useMediaStore from '../../stores/media';
import { createEpubCFI } from '../../util/location';
import useUiStore from '../../stores/ui';

library.add(faBookOpen, faHeadphones);

type Emits = {
  (e: 'switchBookPlayerMode', event: SwitchBookPlayerModeEvent): void;
};
const emit = defineEmits<Emits>();

type Props = {
  showTooltips?: boolean;
  btnSize?: number;
  iconSize?: FontAwesomeIconSizes;
};
const props = withDefaults(defineProps<Props>(), {
  showTooltips: true,
  btnSize: 48,
  iconSize: 'xl',
});

const { t } = useLocale();
const { isAudioOnly } = storeToRefs(useReaderStore());
const { smAndDown, sm, xs } = useDisplay();
const { currentTrack, playing, currentPlayerTime, hasMediaAvailable } = storeToRefs(useMediaStore());
const { isTopBarTransparent, switchModeAvailable } = storeToRefs(useUiStore());

const label = computed(() =>
  isAudioOnly.value ? t('book_player.label_reading_mode') : t('book_player.label_audio_mode'),
);

const icon = computed(() => (isAudioOnly.value ? 'far fa-book-open' : 'far fa-headphones'));

const switchBookPlayerMode = () => {
  if (playing.value) {
    playing.value = !playing.value; //pause before switch
  }

  const cfi = currentTrack.value?.cfi;

  // Position is undefined when no playback has started yet (e.g. book just opened).
  // The receiving view falls back to the API's last saved position in that case.
  const position = cfi
    ? currentPlayerTime.value
      ? createEpubCFI({ epubCFI: cfi, temporalOffsetSeconds: currentPlayerTime.value })
      : createEpubCFI({ epubCFI: cfi })
    : undefined;

  emit('switchBookPlayerMode', { position });
};
</script>

<template>
  <v-btn
    v-if="switchModeAvailable && (isAudioOnly || hasMediaAvailable)"
    data-testid="bp-switch-mode-btn"
    color="on-brand"
    v-bind="{
      ...props,
    }"
    variant="text"
    :ripple="false"
    :icon="!isAudioOnly"
    :aria-label="label"
    :title="label"
    :height="btnSize"
    :width="isAudioOnly && !smAndDown ? '190' : isAudioOnly && sm ? '120' : btnSize"
    @click="switchBookPlayerMode"
    :slim="isAudioOnly && smAndDown"
    :class="{ 'bg-inverse-on-surface ml-2': !isAudioOnly && xs && isTopBarTransparent }"
  >
    <div :class="{ 'd-flex flex-column flex-md-row align-center ga-2 overflow-hidden': isAudioOnly }">
      <FontAwesomeIcon :icon="icon" :size="iconSize" />

      <span v-if="isAudioOnly" class="text-truncate" :class="smAndDown ? 'text-body-2 w-100' : 'text-body-1'">
        {{ label }}
      </span>
    </div>
    <v-tooltip :disabled="!showTooltips" activator="parent" location="top" :text="label" />
  </v-btn>
</template>

<style scoped lang="scss">
:deep(.v-btn__content) {
  overflow: hidden;
}
</style>
